import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';

import { PATCH_CHECK } from '../icons';
import { getFileObjectUrl } from '../../services/files';
import { safeHref } from '../../utils/safeHref';
import ExternalLink from '../common/ExternalLink';

// KPR-385. "Published by keepr ✓" — who stands behind a Discover package or a
// global collection, wherever one is shown: a Discover tile, the package
// page, a global collection's header and About. One component so the words
// and the mark never drift between them (catalog plan C3).
//
// The publisher's name arrives from the API as text and is only ever a React
// text child here — the API refuses markup in it, and this is the second
// wall. The mark is shown only when the API says `verified` (only an admin
// sets it); it carries its meaning in words for a screen reader and a
// tooltip, not in the icon alone.

/** An uploaded logo through the authenticated files client; an address as is. */
function PublisherLogo({ logo, name }) {
	const [src, setSrc] = useState(null);
	const fileId = logo?.fileId || null;
	const url = logo?.url ? safeHref(logo.url) : null;
	useEffect(() => {
		let live = true;
		setSrc(null);
		if (fileId) {
			getFileObjectUrl(fileId, 'sm').then(value => { if (live) setSrc(value); }).catch(() => { if (live) setSrc(null); });
		} else if (url && url.startsWith('https://')) {
			setSrc(url);
		}
		return () => { live = false; };
	}, [fileId, url]);
	if (!src) return null;
	// The name beside it says who; the image is decoration (alt="").
	// no-referrer: a third-party logo address learns nothing of the page.
	return <img className="published-by__logo" src={src} alt="" referrerPolicy="no-referrer" data-name={name} />;
}

/**
 * `publisher` is the API's public view `{ _id, key, name, logo, website,
 * verified }`, or null — then nothing renders. `logo` shows the publisher's
 * logo before the words (the package page and a collection's header, where
 * there is room; never a tile). `link` makes the name a link to its website.
 * `bare` leaves out "Published by" where a label already says it.
 */
export default function PublishedBy({ publisher, logo = false, link = false, bare = false, className = '' }) {
	if (!publisher || !publisher.name) return null;
	const href = link ? safeHref(publisher.website) : null;
	const name = href
		? <ExternalLink href={href} rel="noopener noreferrer nofollow">{publisher.name}</ExternalLink>
		: publisher.name;
	return (
		<span className={['published-by', className].filter(Boolean).join(' ')} data-testid="published-by">
			{logo && publisher.logo ? <PublisherLogo logo={publisher.logo} name={publisher.name} /> : null}
			{/* `bare`: the name alone, where a label already says "Publisher". */}
			<span className="published-by__text">{bare ? null : 'Published by '}{name}</span>
			{publisher.verified ? (
				<Tooltip title="Verified publisher">
					<span className="published-by__mark" role="img" aria-label="Verified publisher" data-testid="published-by-verified">
						<PATCH_CHECK aria-hidden="true" />
					</span>
				</Tooltip>
			) : null}
		</span>
	);
}
